import { jsStringEscape, stringify } from '@internals/utils'
import { ast, syncSchemaRef } from 'kubb/kit'
import type { ResolverTs } from './types.ts'

/**
 * Tells whether a `const` (single-value enum) should render as a bare literal type (`'active'`)
 * rather than a named enum reference or a runtime enum declaration.
 *
 * The parser folds `const` into a single-value enum node. The adapter decides which schemas are
 * named enums and lists them in `enumSchemaNames`, and references to those names get suffixed (for
 * example `StatusKey`). A const renders as a literal only when the adapter has not registered it as
 * a named enum, which keeps the declaration and its references in sync across adapter versions.
 */
export function isInlineConstEnum(node: ast.EnumSchemaNode, enumSchemaNames?: ReadonlySet<string>): boolean {
  const isConst = (node.namedEnumValues ?? node.enumValues ?? []).length === 1
  return isConst && !(node.name && enumSchemaNames?.has(node.name))
}

/**
 * Collects JSDoc annotation strings for a schema node.
 *
 * Only uses official JSDoc tags from https://jsdoc.app/: `@description`, `@deprecated`, `@default`, `@example`, `@type`.
 * Constraint metadata (min/max length, pattern, multipleOf, min/maxProperties) is emitted as plain-text lines.

 */
function isSchemaOptional(schema: ast.SchemaNode): boolean {
  return Boolean(('optional' in schema && schema.optional) || ('nullish' in schema && schema.nullish))
}

function formatExample(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return String(value)
  }

  const rendered = JSON.stringify(value) ?? String(value)
  return rendered.replaceAll('*/', '*\\/')
}

export function buildPropertyJSDocComments(schema: ast.SchemaNode, optional?: boolean): Array<string | undefined> {
  const meta = syncSchemaRef(schema)

  const isArray = meta?.primitive === 'array'

  const hasDescription = meta && 'description' in meta && meta.description

  const formatComment =
    meta && 'format' in meta && meta.format
      ? hasDescription
        ? // Empty line between description and format
          [' ', `Format: \`${meta.format}\``]
        : ['@description', `Format: \`${meta.format}\``]
      : []

  // OAS 3.1 carries schema examples as an `examples` array, one `@example` line each.
  const exampleValues = meta?.examples ?? []

  const comments = [
    hasDescription ? `@description ${jsStringEscape(meta.description)}` : null,
    ...formatComment,
    meta && 'deprecated' in meta && meta.deprecated ? '@deprecated' : null,
    // minItems/maxItems on arrays should not be emitted as @minLength/@maxLength
    !isArray && meta && 'min' in meta && meta.min !== undefined ? `@minLength ${meta.min}` : null,
    !isArray && meta && 'max' in meta && meta.max !== undefined ? `@maxLength ${meta.max}` : null,
    meta && 'pattern' in meta && meta.pattern ? `@pattern ${meta.pattern}` : null,
    meta && 'default' in meta && meta.default !== undefined
      ? `@default ${'primitive' in meta && meta.primitive === 'string' && typeof meta.default === 'string' ? stringify(meta.default) : meta.default}`
      : null,
    ...exampleValues.map((example) => `@example ${formatExample(example)}`),
  ].filter(Boolean)

  // `@type` merely repeats the TypeScript type already sitting next to the property, so it only
  // earns its place inside a comment block that exists for another reason. Bare, it would just
  // add bytes with no information a reader can't already see in the signature.
  const typeTag =
    comments.length && meta && 'primitive' in meta && meta.primitive
      ? [`@type ${meta.primitive}`, (optional ?? isSchemaOptional(schema)) ? ' | undefined' : null].filter(Boolean).join('')
      : null

  return [...comments, typeTag].filter(Boolean)
}

type BuildParamsSchemaOptions = {
  params: Array<ast.ParameterNode>
}

type BuildOperationSchemaOptions = {
  resolver: ResolverTs
}

/**
 * Builds the object schema for a group of parameters sharing one `in` location (path, query, or
 * header), embedding each param's own schema (and JSDoc) directly rather than referencing a
 * separate per-param type — the group itself is the only type these params get exported as.
 */
export function buildParams({ params }: BuildParamsSchemaOptions): ast.SchemaNode {
  return ast.factory.createSchema({
    type: 'object',
    properties: params.map((param) =>
      ast.factory.createProperty({
        name: param.name,
        required: param.required,
        schema: ast.factory.createSchema({ ...param.schema, optional: !param.required }),
      }),
    ),
  })
}

export function buildResponseUnion(node: ast.OperationNode, { resolver }: BuildOperationSchemaOptions): ast.SchemaNode | null {
  const responsesWithSchema = node.responses.filter((res) => res.content?.some((entry) => entry.schema))

  if (responsesWithSchema.length === 0) {
    return null
  }

  return ast.factory.createSchema({
    type: 'union',
    members: responsesWithSchema.map((res) => ast.factory.createSchema({ type: 'ref', name: resolver.response.status(node, res.statusCode) })),
  })
}
